import { Controller } from "@hotwired/stimulus"

/**
 * Treatment Duration Controller
 * 
 * Calcule automatiquement la durée et le prix cumulés quand des traitements sont sélectionnés.
 * Supporte la sélection multiple de soins.
 * 
 * Targets:
 *   - treatmentSelect: Le select (multiple) des traitements
 *   - durationField: Le champ de durée à auto-remplir
 *   - priceDisplay: Zone pour afficher le prix total
 *   - treatmentsSummary: Zone pour afficher le résumé des soins sélectionnés
 *   - depositBanner: Banner d'alerte acompte
 *   - depositAmount: Montant de l'acompte dans le banner
 * 
 * Values:
 *   - treatmentData: Object JSON avec les données des traitements { id: { default_duration, name, price } }
 *   - depositConfig: Object JSON { threshold_minutes, amount_per_hour }
 */
export default class extends Controller {
  static targets = ["treatmentSelect", "durationField", "priceDisplay", "treatmentsSummary", "depositBanner", "depositAmount"]
  static values = { 
    treatmentData: Object,
    depositConfig: Object
  }

  connect() {
    console.log("TreatmentDuration controller connected")
    console.log("Treatment data:", this.treatmentDataValue)
    console.log("Deposit config:", this.depositConfigValue)

    // Initialiser si des traitements sont déjà sélectionnés
    this.calculateTotals()

    // Écouter les changements de durée manuelle
    if (this.hasDurationFieldTarget) {
      this.durationFieldTarget.addEventListener("change", () => this.updateDepositBanner(parseInt(this.durationFieldTarget.value) || 0))
    }

    // Écouter les changements du toggle skip_deposit
    const skipDepositInput = document.getElementById("appointment_skip_deposit")
    if (skipDepositInput) {
      skipDepositInput.addEventListener("change", () => this.updateDepositBanner(parseInt(this.durationFieldTarget?.value) || 0))
    }
  }

  /**
   * Appelé lors du changement de traitement (un ou plusieurs)
   */
  treatmentChanged(event) {
    console.log("Treatment selection changed")
    this.calculateTotals()
  }

  /**
   * Supprime un traitement de la sélection
   */
  removeTreatment(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const treatmentId = event.currentTarget.dataset.treatmentId
    console.log(`Removing treatment: ${treatmentId}`)
    
    if (!this.hasTreatmentSelectTarget) return
    
    // Trouver l'option dans le select caché et la désélectionner
    const select = this.treatmentSelectTarget
    const option = Array.from(select.options).find(opt => opt.value === treatmentId)
    
    if (option) {
      option.selected = false
      // Déclencher l'événement change pour mettre à jour l'affichage du multi-select
      select.dispatchEvent(new Event('change', { bubbles: true }))
      // Recalculer les totaux
      this.calculateTotals()
    }
  }

  /**
   * Calcule la durée et le prix totaux des traitements sélectionnés
   */
  calculateTotals() {
    const selectedIds = this.getSelectedTreatmentIds()
    console.log("Selected treatment IDs:", selectedIds)
    
    if (selectedIds.length === 0) {
      this.updateDuration(30) // Durée par défaut
      this.updatePriceDisplay(0)
      this.updateSummary([])
      this.updateDepositBanner(30)
      return
    }

    let totalDuration = 0
    let totalPrice = 0
    const selectedTreatments = []

    selectedIds.forEach(id => {
      const treatmentData = this.treatmentDataValue[id.toString()]
      if (treatmentData) {
        totalDuration += treatmentData.default_duration || 0
        totalPrice += treatmentData.price || 0
        selectedTreatments.push({ ...treatmentData, id: id })
      }
    })

    console.log(`Total duration: ${totalDuration} min, Total price: ${totalPrice} €`)
    
    this.updateDuration(totalDuration)
    this.updatePriceDisplay(totalPrice)
    this.updateSummary(selectedTreatments)
    this.updateDepositBanner(totalDuration)
  }

  /**
   * Récupère les IDs des traitements sélectionnés
   */
  getSelectedTreatmentIds() {
    if (!this.hasTreatmentSelectTarget) return []
    
    const select = this.treatmentSelectTarget
    
    // Cas multi-select natif
    if (select.multiple) {
      return Array.from(select.selectedOptions).map(opt => opt.value).filter(v => v)
    }
    
    // Cas select simple
    return select.value ? [select.value] : []
  }

  /**
   * Met à jour le champ durée et déclenche les événements nécessaires
   */
  updateDuration(duration) {
    if (!this.hasDurationFieldTarget) {
      console.warn("Duration field target not found")
      return
    }

    console.log(`Setting duration to: ${duration} minutes`)
    this.durationFieldTarget.value = duration
    
    // Déclencher l'événement 'change' pour que les autres controllers réagissent
    this.durationFieldTarget.dispatchEvent(new Event('change', { bubbles: true }))
    this.durationFieldTarget.dispatchEvent(new Event('input', { bubbles: true }))
  }

  /**
   * Met à jour l'affichage du prix total
   */
  updatePriceDisplay(price) {
    if (!this.hasPriceDisplayTarget) return
    
    if (price && price > 0) {
      this.priceDisplayTarget.textContent = `Total : ${parseFloat(price).toFixed(2)} €`
      this.priceDisplayTarget.classList.remove("hidden")
    } else {
      this.priceDisplayTarget.textContent = ""
      this.priceDisplayTarget.classList.add("hidden")
    }
  }

  /**
   * Met à jour le résumé des soins sélectionnés avec bouton de suppression
   */
  updateSummary(treatments) {
    if (!this.hasTreatmentsSummaryTarget) return

    if (treatments.length === 0) {
      this.treatmentsSummaryTarget.innerHTML = ""
      this.treatmentsSummaryTarget.classList.add("hidden")
      return
    }

    const summaryHtml = treatments.map(t => 
      `<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-sz-primary-100 text-sz-primary-800 group">
        ${t.name} <span class="ml-1 text-gray-500">(${t.default_duration} min - ${parseFloat(t.price).toFixed(2)} €)</span>
        <button type="button" 
                class="ml-1 -mr-1 p-0.5 rounded-full text-red-400 hover:text-red-600 hover:bg-red-100 focus:outline-none focus:ring-1 focus:ring-red-500 transition-colors"
                data-action="click->treatment-duration#removeTreatment"
                data-treatment-id="${t.id}"
                title="Retirer ce soin">
          <svg class="w-3 h-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </span>`
    ).join(" ")

    this.treatmentsSummaryTarget.innerHTML = summaryHtml
    this.treatmentsSummaryTarget.classList.remove("hidden")
  }

  /**
   * Met à jour le banner d'acompte en fonction de la durée
   */
  updateDepositBanner(duration) {
    if (!this.hasDepositBannerTarget || !this.hasDepositAmountTarget) return
    if (!this.depositConfigValue || !this.depositConfigValue.threshold_minutes) return

    const skipDepositChecked = this.getSkipDepositChecked()
    const threshold = this.depositConfigValue.threshold_minutes
    const amountPerHour = this.depositConfigValue.amount_per_hour

    if (duration >= threshold && !skipDepositChecked) {
      const depositAmount = (Math.ceil(duration / 60) * amountPerHour).toFixed(2)
      this.depositAmountTarget.textContent = `${depositAmount} €`
      this.depositBannerTarget.classList.remove("hidden")
    } else {
      this.depositBannerTarget.classList.add("hidden")
    }
  }

  /**
   * Vérifie si le toggle skip_deposit est activé
   */
  getSkipDepositChecked() {
    const skipDepositInput = document.getElementById("appointment_skip_deposit")
    return skipDepositInput ? skipDepositInput.checked : false
  }
}
